import {
  CAccordion,
  CAccordionBody,
  CAccordionHeader,
  CAccordionItem,
  CCloseButton,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CSpinner,
  CTable,
  CTableBody,
  CTableDataCell,
  CTableHead,
  CTableHeaderCell,
  CTableRow,
} from "@coreui/react";
import {
  CalendarDaysIcon,
  ClockIcon,
  CreditCardIcon,
  EnvelopeIcon,
  MapPinIcon,
  PhoneIcon,
  UsersIcon,
} from "@heroicons/react/24/solid";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import { Link, useSearchParams } from "react-router-dom";
import { HeaderSort } from "../../components/ui/TableHeaderSort";
import { BookingStatus, BookingTypes } from "../../helpers/enums";
import {
  asActivity,
  Chip,
  formatDateString,
  getActivePrice,
  getActivityView,
  getBadgeClassNameByBookingStatus,
  getBookingHeader,
  getBookingStatus,
  getBookingStatusDisplayValue,
  getPaymentMethodLabel,
  isActivityBooking,
} from "../../helpers/general";
import { useAuth } from "../../hooks/useAuth";
import { useCookie } from "../../hooks/useCookie";
import { useQueryParam } from "../../hooks/useQueryParam";
import { useToast } from "../../hooks/useToast";
import { general } from "../../locales/general";
import apiService from "../../service/apiService";
import { tdStyle, thStyle } from "../../helpers/constants";
import { cardStyle } from "../Places/PlacesTable";

interface TableBodyProps {
  booking: Booking;
  showDetails: (booking: Booking) => void;
  mode: "actions" | "list";
  showItemColumn: boolean;
  dateField: "date" | "createdAt";
}

function Initials({ name, size = 32 }: { name?: string; size?: number }) {
  return (
    <span
      className="d-inline-flex align-items-center justify-content-center rounded-circle flex-shrink-0 fw-bold"
      style={{
        width: size,
        height: size,
        fontSize: size * 0.4,
        color: "#f0813f",
        backgroundColor: "rgba(240,129,63,0.14)",
      }}
    >
      {(name?.[0] ?? "?").toUpperCase()}
    </span>
  );
}

function SortTh({
  label,
  field,
  sort,
  setSort,
  resetSort,
}: {
  label: string;
  field?: string;
  sort?: { field: string; order: string };
  setSort?: (s: { field: string; order: string }) => void;
  resetSort?: () => void;
}) {
  return (
    <CTableHeaderCell scope="col" style={thStyle}>
      <div className="d-flex align-items-center gap-1">
        {label}
        {field && sort && setSort && resetSort && (
          <HeaderSort
            field={field}
            sort={sort.order}
            sortedField={sort}
            setSort={(f, order) => setSort({ field: f, order })}
            clearSort={resetSort}
            defaultField="createdAt"
          />
        )}
      </div>
    </CTableHeaderCell>
  );
}

/* -------------------------------------------------------------------------- */
/*  Table row                                                                 */
/* -------------------------------------------------------------------------- */

function TableBodyRowContent({
  booking,
  showDetails,
  mode,
  showItemColumn,
  dateField,
}: TableBodyProps) {
  const { token } = useCookie("vToken");
  const queryClient = useQueryClient();
  const { toastSuccess, toastError } = useToast();
  const { isAdmin, isPlaceAdmin } = useAuth();

  const AcceptBooking = useMutation((id: string) =>
    apiService.MakePutRequest(`booking/${id}/approve`, {}, token),
  );
  const RefuseBooking = useMutation((id: string) =>
    apiService.MakePutRequest(`booking/${id}/refuse`, {}, token),
  );

  const accept = () =>
    AcceptBooking.mutate(booking.id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        toastSuccess("Réservation acceptée");
        queryClient.invalidateQueries(["getAllBookings"]);
      },
    });

  const refuse = () =>
    RefuseBooking.mutate(booking.id, {
      onError: () => toastError(general.fr.message.operationFailed),
      onSuccess() {
        toastSuccess("Réservation refusée");
        queryClient.invalidateQueries(["getAllBookings"]);
      },
    });

  const header = getBookingHeader(booking);
  const status = getBookingStatus(booking);
  const canAccept = status === BookingStatus.WAITING;
  const canRefuse =
    status === BookingStatus.WAITING || status === BookingStatus.CONFIRMED;

  return (
    <CTableRow
      role="button"
      tabIndex={0}
      onClick={() => showDetails(booking)}
      onKeyDown={(e: React.KeyboardEvent) => {
        // Only react when the row itself is focused (not inner buttons/links)
        if (
          e.target === e.currentTarget &&
          (e.key === "Enter" || e.key === " ")
        ) {
          e.preventDefault();
          showDetails(booking);
        }
      }}
      style={{ cursor: "pointer" }}
    >
      {/* Item (establishment / activity) */}
      {showItemColumn && (
        <CTableDataCell style={tdStyle}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ minWidth: 180 }}
          >
            <img
              src={
                header.image ??
                `https://ui-avatars.com/api/?name=${encodeURIComponent(
                  header.title ?? "",
                )}`
              }
              alt=""
              width={38}
              height={38}
              className="flex-shrink-0"
              style={{ borderRadius: 10, objectFit: "cover" }}
            />
            <div style={{ minWidth: 0 }}>
              <div
                className="fw-semibold text-capitalize text-truncate"
                style={{ maxWidth: 220 }}
              >
                {header.title}
              </div>
              {header.subtitle && (
                <div
                  className="text-muted text-truncate"
                  style={{ fontSize: 12, maxWidth: 220 }}
                >
                  {header.subtitle}
                </div>
              )}
            </div>
          </div>
        </CTableDataCell>
      )}

      {/* Date */}
      <CTableDataCell style={tdStyle}>
        <div className="fw-semibold text-capitalize">
          {formatDateString(
            (dateField === "createdAt" ? booking?.createdAt : booking?.date)!,
          )}
        </div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {dateField === "createdAt"
            ? booking?.createdAt && format(new Date(booking.createdAt), "HH:mm")
            : booking?.hour}
        </div>
      </CTableDataCell>

      {/* User */}
      {mode === "actions" && (
        <CTableDataCell style={tdStyle}>
          <div
            className="d-flex align-items-center gap-2"
            style={{ minWidth: 150 }}
          >
            <Initials name={booking.user?.fullName} />
            {isAdmin ? (
              <Link
                to={`/users/${booking?.user?.id}`}
                className="fw-semibold text-capitalize"
                onClick={(e) => e.stopPropagation()}
              >
                {booking.user?.fullName}
              </Link>
            ) : (
              <span className="fw-semibold text-capitalize">
                {booking.user?.fullName}
              </span>
            )}
          </div>
        </CTableDataCell>
      )}

      {/* Reference */}
      <CTableDataCell style={tdStyle}>
        <span
          className="rounded-2 px-2 py-1"
          style={{
            fontFamily: "monospace",
            fontSize: 12,
            backgroundColor: "#f5f5f5",
            whiteSpace: "nowrap",
          }}
        >
          {booking?.reference}
        </span>
      </CTableDataCell>

      {/* Scan (place admin) */}
      {isPlaceAdmin && (
        <CTableDataCell style={tdStyle}>
          {booking?.scanedBy ? (
            <>
              <div className="fw-semibold text-capitalize">
                {booking.scanedBy.fullName}
              </div>
              {booking?.scanedAt && (
                <div className="text-muted" style={{ fontSize: 12 }}>
                  {format(new Date(booking.scanedAt), "dd/MM/yyyy · HH:mm")}
                </div>
              )}
            </>
          ) : (
            <span className="text-muted">—</span>
          )}
        </CTableDataCell>
      )}

      {/* Status */}
      <CTableDataCell style={tdStyle}>
        <span className={getBadgeClassNameByBookingStatus(booking)}>
          {getBookingStatusDisplayValue(booking)}
        </span>
      </CTableDataCell>

      {/* Payment */}
      <CTableDataCell style={tdStyle}>
        <Chip>{getPaymentMethodLabel(booking)}</Chip>
      </CTableDataCell>

      {/* Source (admin) */}
      {isAdmin && (
        <CTableDataCell style={tdStyle}>
          {booking?.bookingSource ? (
            <Chip tone="warning">{booking.bookingSource}</Chip>
          ) : (
            <span className="text-muted">—</span>
          )}
        </CTableDataCell>
      )}

      {/* Actions — clicks here must not open the details */}
      <CTableDataCell style={tdStyle} onClick={(e) => e.stopPropagation()}>
        <div className="d-flex align-items-center justify-content-end gap-2">
          {canAccept && (
            <button
              className="btn btn-sm btn-primary rounded-pill text-white px-3"
              onClick={accept}
              disabled={AcceptBooking.isLoading}
            >
              {AcceptBooking.isLoading ? <CSpinner size="sm" /> : "Accepter"}
            </button>
          )}
          {canRefuse && (
            <button
              className="btn btn-sm btn-outline-danger rounded-pill px-3"
              onClick={refuse}
              disabled={RefuseBooking.isLoading}
            >
              {RefuseBooking.isLoading ? <CSpinner size="sm" /> : "Refuser"}
            </button>
          )}
          <button
            onClick={() => showDetails(booking)}
            className="btn btn-sm rounded-pill border px-3"
          >
            Vue
          </button>
        </div>
      </CTableDataCell>
    </CTableRow>
  );
}

/* -------------------------------------------------------------------------- */
/*  Details panel – small building blocks                                     */
/* -------------------------------------------------------------------------- */

const sectionTitleStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: "#888",
  marginBottom: 10,
};

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-4">
      <div style={sectionTitleStyle}>{title}</div>
      <div style={cardStyle}>{children}</div>
    </section>
  );
}

function InfoItem({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="d-flex align-items-start gap-2" style={{ minWidth: 0 }}>
      {icon && (
        <span className="text-muted flex-shrink-0 mt-1" style={{ width: 16 }}>
          {icon}
        </span>
      )}
      <div style={{ minWidth: 0 }}>
        <div className="text-muted" style={{ fontSize: 11 }}>
          {label}
        </div>
        <div className="fw-semibold text-break" style={{ fontSize: 13 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

function LineItem({
  label,
  amount,
  currency,
  muted,
}: {
  label: React.ReactNode;
  amount?: number;
  currency: string;
  muted?: boolean;
}) {
  return (
    <div
      className="d-flex align-items-center justify-content-between gap-3 py-1"
      style={{ fontSize: 13, color: muted ? "#777" : undefined }}
    >
      <span>{label}</span>
      {amount !== undefined && (
        <span className="fw-semibold text-nowrap">
          {amount} {currency}
        </span>
      )}
    </div>
  );
}

/**
 * Subtotal / discount / total block shared by every booking kind.
 * Subtotal and discount are only shown when a promo code was applied.
 */
function PriceSummary({
  booking,
  subtotal,
  total,
  currency,
}: {
  booking: Booking;
  subtotal?: number;
  total: number | string;
  currency: string;
}) {
  const promo = booking?.appliedPromoCodes?.[0];
  const showDiscount = !!promo && subtotal !== undefined;

  return (
    <div className="mt-2 pt-2" style={{ borderTop: "1px dashed #ddd" }}>
      {showDiscount && (
        <>
          <LineItem
            label="Sous-total"
            amount={subtotal}
            currency={currency}
            muted
          />
          <div
            className="d-flex align-items-center justify-content-between gap-3 py-1"
            style={{ fontSize: 13, color: "#198754" }}
          >
            <span>Remise ({promo?.discount}%)</span>
            <span className="fw-semibold text-nowrap">
              − {subtotal! - Number(booking?.priceToPay ?? 0)} {currency}
            </span>
          </div>
        </>
      )}
      <div className="d-flex align-items-center justify-content-between gap-3 pt-1">
        <span className="fw-bold">Total</span>
        <span className="fw-bold" style={{ fontSize: 17, color: "#f0813f" }}>
          {total} {currency}
        </span>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/*  Details panel – booking kinds                                             */
/* -------------------------------------------------------------------------- */

function ActivityBookingDetails({ booking }: { booking: Booking }) {
  const view = getActivityView(booking);
  const { pickupLocation, pickupLocationUnknown } = asActivity(booking);

  const isEuro = booking.priceToPayCurrency === "euro";
  const currency = isEuro ? "€" : "DH";

  const lineTotal = (ob: (typeof view.optionBookings)[number]) => {
    const unit = isEuro
      ? Number(ob.ageGroupSnapshot?.priceInEuro ?? ob.unitPrice ?? 0)
      : Number(ob.unitPrice ?? ob.ageGroupSnapshot?.price ?? 0);
    return unit * ob.numberOfReservation;
  };

  const subtotal = view.optionBookings.reduce((t, ob) => t + lineTotal(ob), 0);

  const hasCoordinates =
    pickupLocation?.latitude != null && pickupLocation?.longitude != null;

  return (
    <Section title="Activité réservée">
      <div className="d-flex align-items-center gap-3 mb-3">
        {view.image && (
          <img
            src={view.image}
            alt=""
            width={52}
            height={52}
            style={{ objectFit: "cover", borderRadius: 10 }}
          />
        )}
        <div style={{ minWidth: 0 }}>
          <div className="fw-bold">{view.title ?? "Activité"}</div>
          {view.options.length > 0 && (
            <div className="text-muted" style={{ fontSize: 12 }}>
              Option : {view.options.join(", ")}
            </div>
          )}
        </div>
      </div>

      {view.optionBookings.map((ob) => (
        <LineItem
          key={ob.id}
          label={`${ob.numberOfReservation} × ${ob.ageGroupSnapshot?.label ?? ""}`}
          amount={lineTotal(ob)}
          currency={currency}
        />
      ))}

      {(pickupLocation || pickupLocationUnknown) && (
        <div
          className="d-flex align-items-start gap-2 mt-3 p-2 rounded-3"
          style={{ backgroundColor: "#f8f8f8", fontSize: 13 }}
        >
          <MapPinIcon width={16} className="flex-shrink-0 mt-1 text-muted" />
          <span>
            {pickupLocationUnknown ? (
              "Lieu de prise en charge non précisé"
            ) : pickupLocation?.address ? (
              <>Prise en charge : {pickupLocation.address}</>
            ) : hasCoordinates ? (
              <a
                href={`https://www.google.com/maps?q=${pickupLocation?.latitude},${pickupLocation?.longitude}`}
                target="_blank"
                rel="noreferrer"
              >
                Voir le lieu de prise en charge
              </a>
            ) : null}
          </span>
        </div>
      )}

      <PriceSummary
        booking={booking}
        subtotal={subtotal}
        total={booking?.priceToPay}
        currency={currency}
      />
    </Section>
  );
}

function PacksDetails({ booking }: { booking: Booking }) {
  const isEuro = booking.priceToPayCurrency === "euro";
  return (
    <Section title="Pack réservé">
      <CAccordion flush>
        {booking.packsBookings.map((pack) => {
          const p = pack.pack as any;
          const amount = isEuro
            ? p.priceInEuro * pack.numberOfReservation
            : p.price * pack.numberOfReservation;
          return (
            <CAccordionItem key={pack.id}>
              <CAccordionHeader>
                <div className="d-flex align-items-center justify-content-between gap-2 w-100 pe-2">
                  <div className="d-flex align-items-center gap-2">
                    <img
                      src={pack.pack.image}
                      alt=""
                      width={40}
                      height={40}
                      style={{ objectFit: "cover", borderRadius: 8 }}
                    />
                    <span className="fw-semibold">
                      {pack.numberOfReservation} × {pack.pack.title}
                    </span>
                  </div>
                  <span className="fw-semibold text-nowrap">
                    {amount} {isEuro ? "€" : "DH"}
                  </span>
                </div>
              </CAccordionHeader>
              <CAccordionBody>
                <ul className="mb-0 ps-3">
                  {pack.pack.items.map((packItem) => (
                    <li key={packItem.id} className="mb-1">
                      {packItem.title}
                    </li>
                  ))}
                </ul>
              </CAccordionBody>
            </CAccordionItem>
          );
        })}
      </CAccordion>
    </Section>
  );
}

function VariantsDetails({ booking }: { booking: Booking }) {
  const isEuro = booking.priceToPayCurrency === "euro";
  const currency = isEuro ? "€" : "DH";

  const unitOf = (v: any) =>
    isEuro
      ? getActivePrice(booking, v.priceInEuro, v.nightPriceInEuro)
      : getActivePrice(booking, v.price, v.nightPrice);

  const subtotal = booking.variantsBookings.reduce(
    (t, item) => t + unitOf(item.variant) * item.numberOfReservation,
    0,
  );

  return (
    <Section title="Variant(s) réservé(s)">
      {booking.variantsBookings.map((item) => (
        <LineItem
          key={item.id}
          label={`${item.numberOfReservation} × ${item.variant.description}`}
          amount={item.numberOfReservation * unitOf(item.variant)}
          currency={currency}
        />
      ))}
      <PriceSummary
        booking={booking}
        subtotal={subtotal}
        total={booking?.priceToPay}
        currency={currency}
      />
    </Section>
  );
}

function SpecialOfferDetails({ booking }: { booking: Booking }) {
  const isEuro = booking.priceToPayCurrency === "euro";
  const currency = isEuro ? "€" : "DH";
  const special: any = booking.special;

  const quantity = special?.numberOfReservation || booking.personsNumber;
  const unit = isEuro
    ? getActivePrice(booking, special?.priceInEuro, special?.nightPriceInEuro)
    : getActivePrice(booking, special?.price, special?.nightPrice);
  const subtotal = quantity * unit;

  return (
    <Section title="Offre réservée">
      <LineItem
        label={
          <span className="fw-semibold">
            {special?.title || booking?.event?.name || "Offre"}
          </span>
        }
        currency={currency}
      />
      <PriceSummary
        booking={booking}
        subtotal={subtotal}
        total={booking?.priceToPay}
        currency={currency}
      />
    </Section>
  );
}

function ServicesDetails({ booking }: { booking: Booking }) {
  const isEuro = booking.priceToPayCurrency === "euro";
  const currency = isEuro ? "€" : "DH";

  const unitOf = (s: any) => (isEuro ? s.startingPriceEuro : s.startingPrice);
  const subtotal = booking.servicesBookings.reduce(
    (t, s) => t + unitOf(s.service) * s.personsNumber,
    0,
  );

  return (
    <Section title="Service réservé">
      {booking.servicesBookings.map((item) => (
        <LineItem
          key={item.id}
          label={`${item.personsNumber} × ${item.service.label}`}
          amount={item.personsNumber * unitOf(item.service)}
          currency={currency}
        />
      ))}
      <PriceSummary
        booking={booking}
        subtotal={subtotal}
        total={booking?.priceToPay ?? subtotal}
        currency={currency}
      />
    </Section>
  );
}

/* -------------------------------------------------------------------------- */
/*  Details panel (offcanvas content)                                         */
/* -------------------------------------------------------------------------- */

function BookingDetails({ booking }: { booking: Booking }) {
  const header = getBookingHeader(booking);
  const showPersons =
    booking.type === BookingTypes.PLACE_BOOKING ||
    (booking.type === BookingTypes.SPECIAL_OFFER_BOOKING &&
      booking.variantsBookings?.length === 0);

  const hasVariants = (booking.variantsBookings?.length ?? 0) > 0;
  const hasPacks = (booking.packsBookings?.length ?? 0) > 0;
  const hasServices = (booking.servicesBookings?.length ?? 0) > 0;

  return (
    <>
      {/* Summary */}
      <div className="mb-4">
        <div className="d-flex align-items-start justify-content-between gap-3">
          <h6 className="mb-0 fw-bold text-capitalize" style={{ fontSize: 17 }}>
            {header.title}
          </h6>
          <span className={getBadgeClassNameByBookingStatus(booking)}>
            {getBookingStatusDisplayValue(booking)}
          </span>
        </div>
        <div className="mt-2">
          <span
            className="rounded-2 px-2 py-1"
            style={{
              fontFamily: "monospace",
              fontSize: 12,
              backgroundColor: "#f5f5f5",
            }}
          >
            {booking.reference}
          </span>
        </div>
      </div>

      {/* Customer */}
      <Section title="Client">
        <div className="d-flex align-items-center gap-3 mb-3">
          <Initials name={booking.user?.fullName} size={40} />
          <div className="fw-bold text-capitalize">
            {booking.user?.fullName}
          </div>
        </div>
        <div className="d-flex flex-column gap-2">
          {booking.user?.phone && (
            <InfoItem icon={<PhoneIcon width={16} />} label="Téléphone">
              <a href={`tel:${booking.user.phone}`}>{booking.user.phone}</a>
            </InfoItem>
          )}
          {booking.user?.email && (
            <InfoItem icon={<EnvelopeIcon width={16} />} label="Email">
              <a href={`mailto:${booking.user.email}`}>{booking.user.email}</a>
            </InfoItem>
          )}
        </div>
      </Section>

      {/* Key info */}
      <Section title="Informations">
        <div
          className="d-grid gap-3"
          style={{ gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }}
        >
          {booking.date && (
            <InfoItem icon={<CalendarDaysIcon width={16} />} label="Date">
              {formatDateString(booking.date)}
            </InfoItem>
          )}
          {booking.hour && (
            <InfoItem icon={<ClockIcon width={16} />} label="Heure">
              {booking.hour}
            </InfoItem>
          )}
          <InfoItem icon={<CreditCardIcon width={16} />} label="Paiement">
            {getPaymentMethodLabel(booking)}
          </InfoItem>
          {showPersons && (
            <InfoItem icon={<UsersIcon width={16} />} label="Personnes">
              {booking.personsNumber}
            </InfoItem>
          )}
        </div>
      </Section>

      {/* Booked items */}
      {isActivityBooking(booking) && (
        <ActivityBookingDetails booking={booking} />
      )}
      {hasPacks && <PacksDetails booking={booking} />}
      {hasVariants ? (
        <VariantsDetails booking={booking} />
      ) : booking.special ? (
        <SpecialOfferDetails booking={booking} />
      ) : null}
      {hasServices && <ServicesDetails booking={booking} />}

      {/* Booked for someone else */}
      {booking.bookingFor?.name && (
        <Section title="Réservé pour">
          <div className="d-flex flex-column gap-2">
            <InfoItem label="Nom">{booking.bookingFor.name}</InfoItem>
            {booking.bookingFor.email && (
              <InfoItem label="Email">{booking.bookingFor.email}</InfoItem>
            )}
            {booking.bookingFor.phone && (
              <InfoItem label="Téléphone">{booking.bookingFor.phone}</InfoItem>
            )}
          </div>
        </Section>
      )}

      {/* Scan */}
      {(booking.scanedBy || booking.scanedAt) && (
        <Section title="Scan">
          <div className="d-flex flex-column gap-2">
            {booking.scanedBy && (
              <InfoItem label="Scanné par">
                {booking.scanedBy.fullName}
              </InfoItem>
            )}
            {booking.scanedAt && (
              <InfoItem label="Scanné à">
                {format(new Date(booking.scanedAt), "dd/MM/yyyy - HH:mm")}
              </InfoItem>
            )}
          </div>
        </Section>
      )}

      {/* Meta */}
      {booking.createdAt && (
        <p className="text-muted text-center mb-0" style={{ fontSize: 12 }}>
          Créée le {formatDateString(booking.createdAt)} à{" "}
          {format(new Date(booking.createdAt), "HH:mm")}
        </p>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/*  Table                                                                     */
/* -------------------------------------------------------------------------- */

interface TableProps {
  bookings: Booking[];
  mode?: "actions" | "list";
  dateField?: "date" | "createdAt";
}
export default function BookingTable({
  bookings,
  mode = "actions",
  dateField,
}: TableProps) {
  const [searchParams] = useSearchParams();
  const { add } = useQueryParam();
  const { isAdmin, isPlaceAdmin } = useAuth();
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [sort, setSort] = useState({
    field: "createdAt",
    order: "desc",
  });

  // Admins always see the "Établissement" column. Place admins only get a
  // "Réservation" column when the list contains activity bookings, because
  // that is the only way for them to see which activity / option was booked.
  const hasActivityBookings = bookings?.some((b) => isActivityBooking(b));
  const showItemColumn = Boolean(isAdmin) || Boolean(hasActivityBookings);

  const resetSort = () => setSort({ field: "createdAt", order: "desc" });

  useEffect(() => {
    if (searchParams.get("sort")) {
      const [field, order] = searchParams.get("sort")!.split(":");
      setSort({ field, order });
    } else {
      resetSort();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    add("sort", `${sort.field}:${sort.order}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sort]);

  return (
    <div className="mt-3">
      <CTable responsive hover align="middle" className="mb-0">
        <CTableHead>
          <CTableRow>
            {showItemColumn && (
              <SortTh label={isAdmin ? "Établissement" : "Réservation"} />
            )}
            <SortTh
              label={dateField === "createdAt" ? "Création" : "Date / Heure"}
              field={mode === "actions" ? dateField : undefined}
              sort={sort}
              setSort={setSort}
              resetSort={resetSort}
            />
            {mode === "actions" && <SortTh label="Utilisateur" />}
            <SortTh
              label="Référence"
              field={mode === "actions" ? "reference" : undefined}
              sort={sort}
              setSort={setSort}
              resetSort={resetSort}
            />
            {isPlaceAdmin && <SortTh label="Scanné" />}
            <SortTh label="Statut" />
            <SortTh
              label="Paiement"
              field={mode === "actions" ? "paidInApp" : undefined}
              sort={sort}
              setSort={setSort}
              resetSort={resetSort}
            />
            {isAdmin && (
              <SortTh
                label="Source"
                field={mode === "actions" ? "bookingSource" : undefined}
                sort={sort}
                setSort={setSort}
                resetSort={resetSort}
              />
            )}
            <CTableHeaderCell style={thStyle} />
          </CTableRow>
        </CTableHead>
        <CTableBody>
          {bookings?.map((booking) => (
            <TableBodyRowContent
              key={booking.id}
              booking={booking}
              showDetails={(b: Booking) => setSelectedBooking(b)}
              mode={mode}
              showItemColumn={showItemColumn}
              dateField={dateField!}
            />
          ))}
        </CTableBody>
      </CTable>

      <COffcanvas
        backdrop={true}
        placement="end"
        visible={!!selectedBooking}
        onHide={() => setSelectedBooking(null)}
        style={{ width: 460, maxWidth: "100vw" }}
      >
        <COffcanvasHeader
          className="px-4 py-3"
          style={{ borderBottom: "1px solid #eee" }}
        >
          <COffcanvasTitle className="fw-bold">
            Détails de la réservation
          </COffcanvasTitle>
          <CCloseButton
            className="text-reset"
            onClick={() => setSelectedBooking(null)}
          />
        </COffcanvasHeader>
        <COffcanvasBody
          className="px-4 py-4"
          style={{ backgroundColor: "#fcfcfc" }}
        >
          {selectedBooking && <BookingDetails booking={selectedBooking} />}
        </COffcanvasBody>
      </COffcanvas>
    </div>
  );
}
